/* eslint-disable max-len */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Button, Grid, Icon, Image, Modal, Popup, Segment } from 'semantic-ui-react';
import { connect } from 'react-redux';
import RefundRequestDetail from '../../components/RefundRequestDetail';
import CancelRequestDetail from '../../components/CancelRequestDetail';
import OpportunityCancelModal from '../../components/OpportunityCancelModal';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import { OverviewDetail } from '../UpdatedComponents/UCOverviewDetail';
import StatusFlag from '../UpdatedComponents/Basic/UCBStatusFlag';
import InfoPopup from '../UpdatedComponents/Basic/UCBPopup';
import Timestamp from '../Timestamp';
import DocumentBlackSvg from '../../assets/icons/document_black.svg';
import { flashErrorMessage } from '../../actions/flash';
import CreditCardDisabledSvg from '../../assets/icons/credit_card_disabled.svg';
import CreditCardYellowSvg from '../../assets/icons/credit_card_yellow.svg';
import CreditCardGreenSvg from '../../assets/icons/credit_card_green.svg';
import LoanDisabledSvg from '../../assets/icons/loan_disabled.svg';
import LoanGreen from '../../assets/icons/loan_green.svg';
import { fetchM } from '../../actions/fetchM';
import { SOURCE_OF_FINANCE_STATUS_MAP } from '../../constants/sourceOfFinanceStatuses';
import { OPPORTUNITY_STATUS, OPPORTUNITY_STATUS_MAP, SINGLE_STATUS_MAP } from '../../constants/opportunityStatuses';
import { opportunityCanHaveRefund, refundKeyOfOpportunity, shouldRefetchRefund } from './refundRefetch';

// Named export for lifecycle tests; the default export below is the wrapped one the app uses.
export class OpportunityOverviewDetail extends Component {
  static contextTypes = {
    store: PropTypes.object,
  };

  static propTypes = {
    auth: PropTypes.shape({
      isHeadquarters: PropTypes.bool,
      isAdminBasic: PropTypes.bool,
      isAdmin: PropTypes.bool,
      isSuperAdmin: PropTypes.bool,
    }),
    t: PropTypes.func,
    data: PropTypes.oneOfType([
      PropTypes.object,
    ]),
    paymentData: PropTypes.oneOfType([
      PropTypes.object,
    ]),
    refundData: PropTypes.array,
    refundToken: PropTypes.string,
    fetchRefunds: PropTypes.func,
    fetchBooking: PropTypes.func,
    onUpdateOpportunity: PropTypes.func,
    loading: PropTypes.bool,
  }
  static mapStateToProps = state => ({
    auth: state.auth,
  });
  constructor(props) {
    super(props);
    this.state = {
      isConfirmOpen: false,
      confirmType: null,
      isCancelModalOpen: false,
      isRefundConfirmOpen: false,
    };
  }

  componentDidMount() {
    this.mounted = true;
    const { auth, data, refundData } = this.props;
    const hasPermission = auth && (
      auth.isAdmin ||
      auth.isSuperAdmin ||
      auth.isHeadquarters ||
      auth.isAdminBasic
    );
    // Use refundData passed from parent
    if (hasPermission && data && data.id && refundData && refundData.length > 0) {
      const refund = refundData.find(r => r.opportunityID === data.id);
      if (refund) {
        this.setState({ refundRecord: refund });
      }
    }
    // Read the specific refund record for the latest status, but only when the opportunity's
    // sr_status can carry one; otherwise Book API answers 404 for every read.
    if (hasPermission && data && data.id && opportunityCanHaveRefund(data)) {
      this.fetchSpecificRefund();
    }
  }

  componentDidUpdate(prevProps) {
    const { auth, data, refundData } = this.props;

    const currentId = data && data.id;
    if (!currentId) return;

    const hasPermission = auth && (
      auth.isAdmin ||
      auth.isSuperAdmin ||
      auth.isHeadquarters ||
      auth.isAdminBasic
    );

    if (!hasPermission) {
      this.clearRefundRecord();
      return;
    }

    // This component stays mounted across a booking switch. A record read for the previous
    // booking must never be rendered against the next one, and a late response for it must be
    // dropped, even when the next booking is also in a refund state.
    const prevId = prevProps.data && prevProps.data.id;
    if (String(prevId) !== String(currentId)) this.clearRefundRecord();

    const prevRefundData = prevProps.refundData;

    // Field comparison, not `prevProps.data !== data`: Overview rebuilds `data` as a fresh object
    // on every state reset, which re-issued this GET on every render (CPI-1388). A change in the
    // parent's refund list is still an explicit refresh and still re-reads.
    if (shouldRefetchRefund(prevProps.data, data, refundKeyOfOpportunity) || prevRefundData !== refundData) {
      // Re-read the specific refund record for the latest status, but only for an sr_status that
      // can carry one; any other status would just be another 404.
      if (!opportunityCanHaveRefund(data)) {
        this.clearRefundRecord();
        return;
      }
      if (refundData && refundData.length > 0) {
        const refund = refundData.find(r => r.opportunityID === data.id);
        if (refund) {
          this.setState({ refundRecord: refund });
        }
      }
      this.fetchSpecificRefund();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  clearRefundRecord = () => {
    // Bumping the version also invalidates any in-flight read so a late response is dropped.
    this.refundRequestVersion = (this.refundRequestVersion || 0) + 1;
    if (this.state.refundRecord != null) this.setState({ refundRecord: null });
  }

  fetchSpecificRefund = () => {
    const { store } = this.context;
    const { data } = this.props;
    if (!data || !data.id) return;

    const url = `/unit_reservation_refunds/opportunity/${data.id}/`;
    this.refundRequestVersion = (this.refundRequestVersion || 0) + 1;
    const requestVersion = this.refundRequestVersion;
    const isCurrent = () => this.mounted !== false && requestVersion === this.refundRequestVersion;

    const action = fetchM(url, {
      credentials: 'include',
      method: 'GET',
      ignoreNotFound: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (store) {
      store.dispatch(action)
        .then((response) => {
          if (response && response.ok) {
            return response.json();
          }
          if (response && response.status === 404) {
            return null;
          }
          throw new Error('Network response was not ok');
        })
        .then((record) => {
          if (!isCurrent()) return;
          // A 404 is authoritative: there is no record for this booking, so none may be shown.
          this.setState({ refundRecord: record || null });
        })
        .catch(() => {
        });
    }
  };

  getBookingIcon = (bookingStatus) => {
    switch (bookingStatus) {
      case OPPORTUNITY_STATUS.PAID_UNVERIFIED:
      case OPPORTUNITY_STATUS.PAID_VERIFIED:
      case OPPORTUNITY_STATUS.APPROVED:
      case OPPORTUNITY_STATUS.PAYMENT_MADE:
        return CreditCardGreenSvg;
      case OPPORTUNITY_STATUS.PARTIALLY_PAID_UNVERIFIED:
        return CreditCardYellowSvg;
      default:
        return CreditCardDisabledSvg;
    }
  }

  getSourceOfFinanceIcon = (FinanceSource) => {
    switch (FinanceSource) {
      case 'bank_loan':
        return LoanGreen;
      default:
        return LoanDisabledSvg;
    }
  }

  handleOpenConfirm = (type) => {
    this.setState({ isConfirmOpen: true, confirmType: type });
  }

  handleCloseConfirm = () => {
    this.setState({ isConfirmOpen: false, confirmType: null });
  }

  handleConfirmAction = () => {
    const { onUpdateOpportunity } = this.props;
    const { confirmType } = this.state;

    // trigger onUpdateOpportunity only if both of these values are present
    if (onUpdateOpportunity && confirmType) {
      onUpdateOpportunity(confirmType);
    }
    this.handleCloseConfirm();
  }

  handleSubmitRefundRequest = () => {
    const { store } = this.context;
    const { data, refundToken, paymentData, t, fetchBooking } = this.props;
    const url = '/unit_reservation_refunds/';
    const bodyPayload = {
      opportunityID: data.id,
      amountMinor: paymentData.amountMinorPaid ? paymentData.amountMinorPaid : 0,
      currencyCode: paymentData.currencyCode,
      reason: data.sr_cancellation_reason ? data.sr_cancellation_reason : '-',
      bankName: (data.primary_customer && data.primary_customer.contact) ? data.primary_customer.contact.bank_name : '',
      bankAccountNumber: (data.primary_customer && data.primary_customer.contact) ? data.primary_customer.contact.bank_account_number : '',
      bankAccountName: (data.primary_customer && data.primary_customer.contact) ? data.primary_customer.contact.bank_account_name : '',
      projectName: data.project_name,
      purchaserName: data.primary_customer ? data.primary_customer.name : '',
    };
    const action = fetchM(url, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': refundToken,
      },
      body: JSON.stringify(bodyPayload),
    });

    if (store) {
      store.dispatch(action)
        .then((response) => {
          if (response && response.ok) {
            // Fetch the refund record
            this.fetchSpecificRefund();
            // Refetch booking to get updated sr_status
            if (fetchBooking) {
              fetchBooking();
            }
          }
        })
        .catch((error) => {
          store.dispatch(flashErrorMessage({ message: t('An error occurred. Please try again', { ns: TRANSLATION_FILES.translation }) }));
        });
    }
  }

  handleOpenCancelModal = () => {
    this.setState({ isCancelModalOpen: true });
  }

  handleCloseCancelModal = () => {
    this.setState({ isCancelModalOpen: false });
  }

  handleOpenRefundConfirm = () => {
    this.setState({ isRefundConfirmOpen: true });
  }

  handleCloseRefundConfirm = () => {
    this.setState({ isRefundConfirmOpen: false });
  }

  handleConfirmRefund = () => {
    this.handleSubmitRefundRequest();
    this.setState({ isRefundConfirmOpen: false });
  }


  // CPI-1226: the modal hands back the scenario as the fourth argument (the third is the target).
  handleSubmitCancellationRequest = (status, reason, cancellationType) => {
    const { onUpdateOpportunity } = this.props;
    if (onUpdateOpportunity) onUpdateOpportunity(status, reason, null, cancellationType);
  }

  checkAccess = () => {
    const { auth } = this.props;
    if (auth.isAdminBasic || auth.isAdmin || auth.isSuperAdmin || auth.isHeadquarters) {
      return true;
    }
    return false;
  }

  renderHero = () => (
    <Image src="https://react.semantic-ui.com/images/wireframe/image.png" rounded style={{ width: '60px', height: '60px' }} />
  )


  renderProgressIcons = (data) => {
    const { t } = this.props;
    const iconSize = 18;
    const bookingStatus = data && data.sr_status ? data.sr_status : null;
    const financeStatus = data && data.sr_source_of_finance ? data.sr_source_of_finance : null;
    const bookingIcon = (
      <img
        src={this.getBookingIcon(bookingStatus)}
        width={iconSize}
        alt="booking status"
      />
    );
    const loanIcon = (
      <img
        src={this.getSourceOfFinanceIcon(financeStatus)}
        width={iconSize}
        alt="loan"
      />
    );
    const documentIcon = (
      <img
        // src={this.hasBookingNote(data) ? NotesSvg : NotesDisabledSvg}
        src={DocumentBlackSvg}
        width={iconSize}
        alt="document"
      />
    );
    return (
      <div className="verticalAlign-mid" style={{ display: 'inline-flex', justifyContent: 'start', gap: 8 }}>
        <InfoPopup
          // touchAsClicked={!isComputer}
          trigger={bookingIcon}
          content={`${t(bookingStatus && SINGLE_STATUS_MAP[bookingStatus] ? SINGLE_STATUS_MAP[bookingStatus].text : 'None', { ns: TRANSLATION_FILES.constants })}`}
        />
        <InfoPopup
          trigger={loanIcon}
          content={`${t(financeStatus && SOURCE_OF_FINANCE_STATUS_MAP[financeStatus] ? SOURCE_OF_FINANCE_STATUS_MAP[financeStatus].text : 'None', { ns: TRANSLATION_FILES.constants })}`}
        />
        <InfoPopup
          // touchAsClicked={!isComputer}
          trigger={documentIcon}
          content={`${t('Document uploaded')}`}
        />
      </div>
    );
  }

  renderTitle = (t) => {
    const { data } = this.props;
    const statusId = data.sr_status;
    const currentStatus = SINGLE_STATUS_MAP[statusId] || SINGLE_STATUS_MAP[OPPORTUNITY_STATUS.PENDING_PAYMENT];
    const project = (data && data.project_name) || '';
    const customer = (data && data.primary_customer && data.primary_customer.name) || '';
    return (
      <div style={{ flex: 1, minWidth: 0 }}>
        <Grid verticalAlign="middle" style={{ margin: 0 }}>
          <Grid.Row style={{ padding: 0 }}>
            <Grid.Column width={16} style={{ paddingLeft: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 24, fontWeight: 'bold', wordBreak: 'break-all', flexShrink: 1, minWidth: 0 }}>{project}{project && customer ? ' - ' : ''}{customer}</span>
                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {currentStatus ? <StatusFlag text={t(currentStatus.text, { ns: TRANSLATION_FILES.constants })} value={`opportunity-${currentStatus.value}`} /> : null}
                  {this.renderProgressIcons(data)}
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    );
  }

  renderSubtitle = () => {
    const { data, t } = this.props;

    const record = Array.isArray(data) ? data[0] : data;

    if (!record || !record.created_at) {
      return (
        <span style={{ fontSize: 12, color: '#969CA4' }}>
          {t('Created at', { ns: TRANSLATION_FILES.translation })} --/--/----
        </span>
      );
    }

    if (isNaN(new Date(record.created_at).getTime())) {
      return (
        <span style={{ fontSize: 12, color: '#969CA4' }}>
          {t('Invalid Date', { ns: TRANSLATION_FILES.translation })}
        </span>
      );
    }

    return (
      <span style={{ fontSize: 12, color: '#969CA4' }}>
        {t('Created at', { ns: TRANSLATION_FILES.translation })} <Timestamp timestamp={record.created_at} format="DD/MM/YYYY HH:mm:ss" />
      </span>
    );
  }

  renderCancellationButtons = () => {
    const { loading, t } = this.props;
    return [
      <Button
        key="approve"
        className="btn-outcome btn-initiate-refund"
        basic
        disabled={loading}
        onClick={() => this.handleOpenRefundConfirm()}
      >
        <Icon name="refresh" color="blue" />
        {t('Initiate refund', { ns: TRANSLATION_FILES.translation })}
      </Button>,
    ];
  }

  renderActionButtons = () => {
    const { loading, t } = this.props;
    return [
      <Button
        key="approve"
        className="uc-button"
        basic
        disabled={loading}
        onClick={() => this.handleOpenConfirm(OPPORTUNITY_STATUS.APPROVED)}
      >
        <Icon name="check circle outline" color="green" />
        {t('Approve', { ns: TRANSLATION_FILES.translation })}
      </Button>,
      <Button
        key="cancel-req"
        className="uc-button"
        basic
        disabled={loading}
        onClick={this.handleOpenCancelModal}
      >
        <Icon name="times circle outline" color="red" />
        {t('Cancel', { ns: TRANSLATION_FILES.translation })}
      </Button>,
    ];
  }

  renderOtherActions = () => {
    const { t, data } = this.props;
    const hasLinkedBooking = !!(data && data.booking_id);
    const isCancelDisabled = hasLinkedBooking && data.booking_status_id !== 'cancelled_developer';
    const cancelTooltip = isCancelDisabled
      ? t('Cancel option is disabled as the unit has been reserved', { ns: TRANSLATION_FILES.translation })
      : t('Cancel Booking', { ns: TRANSLATION_FILES.translation });
    const allowedDropdownStatuses = [
      OPPORTUNITY_STATUS.PENDING_PAYMENT,
      OPPORTUNITY_STATUS.PAYMENT_MADE,
      OPPORTUNITY_STATUS.PARTIALLY_PAID_UNVERIFIED,
      OPPORTUNITY_STATUS.PAID_UNVERIFIED,
      OPPORTUNITY_STATUS.PAID_VERIFIED,
      OPPORTUNITY_STATUS.APPROVED,
      OPPORTUNITY_STATUS.PENDING_REFUND,
      OPPORTUNITY_STATUS.VERIFICATION_REJECTED,
    ];

    if (!allowedDropdownStatuses.includes(data.sr_status)) {
      return null;
    }

    const trigger = (
      <span
        onClick={isCancelDisabled ? undefined : this.handleOpenCancelModal}
        aria-label={cancelTooltip}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isCancelDisabled ? 'not-allowed' : 'pointer',
          opacity: isCancelDisabled ? 0.45 : 1,
          userSelect: 'none',
        }}
      >
        <Icon name="ellipsis vertical" color="grey" />
      </span>
    );

    return (
      <Popup
        trigger={trigger}
        content={cancelTooltip}
        position="bottom right"
        size="mini"
        inverted
        on="hover"
      />
    );
  };

  renderConfirmModal = () => {
    const { t } = this.props;
    const { isConfirmOpen, confirmType } = this.state;

    const modalConfigs = {
      [OPPORTUNITY_STATUS.APPROVED]: {
        icon: 'check circle outline',
        color: 'green',
        header: 'Confirm Approval',
        content: 'Are you sure you want to approve this opportunity?',
        buttonText: 'Yes, Approve',
        buttonColor: 'green',
      },
      REJECT_TO_PREVIOUS_STATUS: {
        icon: 'times circle outline',
        color: 'red',
        header: 'Confirm Rejection',
        content: 'Are you sure you want to reject this opportunity?',
        buttonText: 'Yes, Reject',
        buttonColor: 'red',
      },
    };

    const config = modalConfigs[confirmType];

    if (!config) return null;

    return (
      <Modal
        open={isConfirmOpen}
        onClose={this.handleCloseConfirm}
        size="mini"
      >
        <Modal.Header>
          <Icon name={config.icon} color={config.color} />
          {t(config.header, { ns: TRANSLATION_FILES.translation })}
        </Modal.Header>
        <Modal.Content>
          <p>
            {t(config.content, { ns: TRANSLATION_FILES.translation })}
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={this.handleCloseConfirm}>
            {t('Cancel', { ns: TRANSLATION_FILES.translation })}
          </Button>
          <Button
            color={config.buttonColor}
            onClick={this.handleConfirmAction}
          >
            {t(config.buttonText, { ns: TRANSLATION_FILES.translation })}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }

  renderRefundConfirmModal = () => {
    const { t } = this.props;
    const { isRefundConfirmOpen } = this.state;

    return (
      <Modal
        open={isRefundConfirmOpen}
        onClose={this.handleCloseRefundConfirm}
        size="mini"
      >
        <Modal.Header>
          <Icon name="refresh" color="blue" />
          {t('Initiate refund', { ns: TRANSLATION_FILES.translation })}
        </Modal.Header>
        <Modal.Content>
          <p>
            {t('Do you wish to initiate refund?', { ns: TRANSLATION_FILES.translation })}
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={this.handleCloseRefundConfirm}>
            {t('Cancel', { ns: TRANSLATION_FILES.translation })}
          </Button>
          <Button
            color="blue"
            onClick={this.handleConfirmRefund}
          >
            {t('Initiate refund', { ns: TRANSLATION_FILES.translation })}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }

  renderCancelModal = () => {
    const { data } = this.props;
    return (
      <OpportunityCancelModal
        open={this.state.isCancelModalOpen}
        srCode={data.sr_code}
        onClose={this.handleCloseCancelModal}
        onSubmit={this.handleSubmitCancellationRequest}
      />
    );
  }

  render() {
    const { t, data, paymentData = {} } = this.props;
    const { refundRecord } = this.state;
    const isPendingApproval = [OPPORTUNITY_STATUS.PAID_VERIFIED].includes(data.sr_status);
    const isCancellationApproved = [
      OPPORTUNITY_STATUS.CANCELLED,
    ].includes(data.sr_status);
    // The panel carrying the cancellation category, reason, requestor and approved-at.
    const showCancelRequestDetail = [
      OPPORTUNITY_STATUS.PENDING_CANCELLATION,
      OPPORTUNITY_STATUS.CANCELLED,
      OPPORTUNITY_STATUS.CANCELLED_ERROR,
    ].includes(data.sr_status);
    const hasAccess = this.checkAccess();
    let actionButton;
    let otherActions;
    const includeCancelButton = [
      OPPORTUNITY_STATUS.PENDING_PAYMENT,
      OPPORTUNITY_STATUS.PAYMENT_MADE,
      OPPORTUNITY_STATUS.PARTIALLY_PAID_UNVERIFIED,
      OPPORTUNITY_STATUS.PAID_UNVERIFIED,
      OPPORTUNITY_STATUS.PAID_VERIFIED,
      OPPORTUNITY_STATUS.VERIFICATION_REJECTED,
      OPPORTUNITY_STATUS.APPROVED,
    ].includes(data.sr_status);
    if (includeCancelButton) {
      otherActions = this.renderOtherActions();
    } else {
      otherActions = null;
    }


    if (isPendingApproval && hasAccess) {
      actionButton = this.renderActionButtons();
    } else if (isCancellationApproved && hasAccess && paymentData && paymentData.amountMinorPaid > 0) {
      actionButton = this.renderCancellationButtons();
    } else {
      actionButton = null;
    }
    return (
      <React.Fragment>
        <OverviewDetail
          // hero={this.renderHero()} // TODO: temp hide
          title={this.renderTitle(t)}
          subtitle={this.renderSubtitle()}
          actionButtons={actionButton}
          otherActions={otherActions}
        />
        {this.renderConfirmModal()}
        {this.renderRefundConfirmModal()}
        {this.renderCancelModal()}
        {showCancelRequestDetail && (
          <Segment basic style={{ padding: 0 }}>
            <CancelRequestDetail
              data={data}
              hasAccess={hasAccess}
              onUpdateOpportunity={this.props.onUpdateOpportunity}
            />
          </Segment>
        )}
        {(refundRecord != null && (data.sr_status === OPPORTUNITY_STATUS.PENDING_REFUND || data.sr_status === OPPORTUNITY_STATUS.REFUNDED || data.sr_status === OPPORTUNITY_STATUS.REFUND_DECLINED)) && (
          <Segment basic>
            <RefundRequestDetail data={data} refund={refundRecord} refundToken={this.props.refundToken} onUpdateOpportunity={this.props.onUpdateOpportunity} onSuccess={() => this.fetchSpecificRefund()} />
          </Segment>
        )}
      </React.Fragment>
    );
  }
}

export default withTranslation(namespaces)(connect(OpportunityOverviewDetail.mapStateToProps)(OpportunityOverviewDetail));



// WEBPACK FOOTER //
// ./src/components/Opportunities/OpportunityOverviewDetail.jsx